import booleanClockwise from "@turf/boolean-clockwise";

/**
 * Ensure a single polygon ring follows GeoJSON right-hand rule.
 * - Outer ring must be counter-clockwise
 * - Hole must be clockwise
 *
 * @param ring    Coordinates array: [[lon, lat], ...]
 * @param isHole  Set to true if the ring is a hole
 * @returns       Corrected ring
 */

export function fixRingOrientation(
  ring: [number, number][],
  isHole = false
): [number, number][] {
  if (ring.length < 3) {
    return ring; // Need at least 3 points for a valid polygon
  }

  // Calculate the centroid of the ring
  const centroid = ring.reduce(
    (acc, point) => [acc[0] + point[0], acc[1] + point[1]],
    [0, 0]
  );
  centroid[0] /= ring.length;
  centroid[1] /= ring.length;

  // Sort points by angle from centroid
  const sortedRing = [...ring].sort((a, b) => {
    const angleA = Math.atan2(a[1] - centroid[1], a[0] - centroid[0]);
    const angleB = Math.atan2(b[1] - centroid[1], b[0] - centroid[0]);
    return angleA - angleB;
  });

  // Check if the sorted ring is clockwise
  const clockwise = booleanClockwise(sortedRing);
  const shouldBeClockwise = isHole;

  const clockWiseRing =
    clockwise !== shouldBeClockwise ? [...sortedRing].reverse() : sortedRing;

  if (clockWiseRing[0] !== clockWiseRing[clockWiseRing.length - 1]) {
    clockWiseRing.push(clockWiseRing[0]);
  }
  return clockWiseRing;
}
